import React, { useState, useEffect } from "react";
import { useHistory, Link } from "react-router-dom";
import axiosInstance from "../../api/axiosInstance";
import { notification } from "antd";
import Cookies from "js-cookie";
import { HiOutlineMail, HiOutlineLockClosed, HiOutlineEye, HiOutlineEyeOff } from 'react-icons/hi';
import { useTranslation } from "react-i18next";
import { apiurl } from "../../App";
import { initEncryptionSession } from "../../encryptionSession";
import { decryptData } from "../Crypto_Helpers/cryptoHelper";

function Login() {
  const history = useHistory();
  const { t, i18n } = useTranslation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    // Initial language setup
    Cookies.set("pharmacy_language", "en", { expires: 365, sameSite: "Lax", secure: true });
    i18n.changeLanguage("en");

    // Clear old token data
    Cookies.remove("refreshtoken");
    Cookies.remove("tokenHeader");
    Cookies.remove("tokenExpiry");
    localStorage.removeItem("tokenHeader");
    localStorage.removeItem("refreshtoken");

  }, [i18n]);

  const validate = () => {
    const errs = {};
    if (!email.trim()) {
      errs.email = t("email_required") || "Email is required";
    } else if (!/\S+@\S+\.\S+/.test(email)) {
      errs.email = t("email_invalid") || "Please enter a valid email";
    }
    if (!password) {
      errs.password = t("password_required") || "Password is required";
    }
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    if (!validate()) return;

    setLoading(true);
    try {
      const response = await axiosInstance.post("pharmacy/LoginWeb", {
        email,
        password,
        module: "Pharmacy",
      });

      if (response.data.status === 1 && response.data.data) {
        // Login response is in publicUrls so the interceptor skips auto-decryption.
        // Manually decrypt the data field here.
        const aesKey = await initEncryptionSession();
        let dataList = response.data.data;
        if (typeof dataList === "string" && aesKey) {
          try { dataList = await decryptData(dataList, aesKey); } catch (e) { dataList = []; }
        }
        if (!Array.isArray(dataList) || dataList.length === 0) {
          notification.error({ message: response.data.msg || "Login failed. Please try again.", placement: "topRight" });
          setLoading(false);
          return;
        }
        const userData = dataList[0];
        const expiryTime = new Date(Date.now() + 24 * 60 * 60 * 1000); // 24 hours

        Cookies.set("Email", userData.email || "");
        Cookies.set("User_name", userData.user_name || "");
        Cookies.set("refreshtoken", response.data.refreshtoken || "");
        Cookies.set("tokenHeader", response.data.token || "");
        Cookies.set("tokenExpiry", expiryTime.toISOString());
        Cookies.set("vendorId", userData.vendor_id || "");
        Cookies.set("pharmacy_id", userData.vendor_id || "");
        Cookies.set("pharmacy_language", "en");
        if (userData.vendor_filename) {
          Cookies.set("profilePath", userData.vendor_filename);
        }

        // Also persist to localStorage so components and axiosInstance that read localStorage work correctly
        localStorage.setItem("pharmacy_id", userData.vendor_id || "");
        localStorage.setItem("vendorId", userData.vendor_id || "");
        localStorage.setItem("User_name", userData.user_name || "");
        const logoPath = userData.vendor_profile_path || userData.vendor_filename || "";
        if (logoPath) {
          localStorage.setItem("profilePath", logoPath);
          Cookies.set("profilePath", logoPath, { expires: 1 });
        }
        if (response.data.token) localStorage.setItem("tokenHeader", response.data.token);
        if (response.data.refreshtoken) localStorage.setItem("refreshtoken", response.data.refreshtoken);

        notification.success({
          message: t("login_success") || "Login Successful",
          placement: "topRight"
        });

        history.push("/Home/dashboard");
      } else {
        notification.error({
          message: response.data.msg || "Invalid credentials",
          placement: "topRight"
        });
      }
    } catch (err) {
      console.error(err);
      notification.error({
        message: "Login failed. Please check your credentials.",
        placement: "topRight"
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-teal-50/50 flex items-center justify-center p-4">
      <div className="max-w-[950px] w-full bg-white rounded-3xl shadow-2xl overflow-hidden flex flex-col md:flex-row min-h-[520px] border border-teal-100/40">

        {/* Left Side - Brand Identity */}
        <div className="md:w-[42%] bg-teal-600 p-6 lg:p-10 text-white flex flex-col justify-center items-center text-center relative overflow-hidden">
          {/* Subtle Background Pattern */}
          <div className="absolute top-0 left-0 w-full h-full opacity-5 pointer-events-none">
            <svg width="100%" height="100%" xmlns="http://www.w3.org/2000/svg">
              <defs>
                <pattern id="grid" width="40" height="40" patternUnits="userSpaceOnUse">
                  <path d="M 40 0 L 0 0 0 40" fill="none" stroke="white" strokeWidth="1" />
                </pattern>
              </defs>
              <rect width="100%" height="100%" fill="url(#grid)" />
            </svg>
          </div>

          <div className="relative z-10">
            <h2 className="text-3xl font-extrabold mb-2 tracking-tight drop-shadow-sm">Welcome Back!</h2>
            <p className="text-teal-50 text-sm font-medium leading-relaxed max-w-xs mx-auto opacity-90">
              Manage your pharmacy operations efficiently and serve your customers better.
            </p>

            <div className="mt-8 flex flex-col items-center space-y-2">
              <div className="h-1.5 bg-white/20 rounded-full w-24 shadow-inner"></div>
              <div className="h-1.5 bg-white/10 rounded-full w-16 shadow-inner"></div>
            </div>
          </div>
        </div>

        {/* Right Side - Login Form */}
        <div className="md:w-[58%] p-6 lg:p-10 flex flex-col justify-center bg-white">
          <div className="mb-5">
            <h1 className="text-3xl font-extrabold text-gray-900 mb-1 tracking-tight">Login</h1>
            <p className="text-gray-500 text-sm font-medium">Access your dashboard</p>
          </div>

          <form onSubmit={handleLogin} className="space-y-4">
            <div className="space-y-1.5">
              <label className="block text-xs font-bold text-gray-700 ml-1 uppercase tracking-wider">Email Address</label>
              <div className="relative group">
                <div className="absolute inset-y-0 left-0 pl-3.5 flex items-center pointer-events-none">
                  <HiOutlineMail className="h-5 w-5 text-gray-400 group-focus-within:text-teal-600 transition-colors" />
                </div>
                <input
                  type="email"
                  className={`block w-full pl-10 pr-4 py-3 border-2 ${errors.email ? 'border-red-500 bg-red-50/10' : 'border-gray-200 bg-gray-50/50 group-hover:border-gray-300'} rounded-xl focus:ring-4 focus:ring-teal-500/10 focus:border-teal-500 focus:bg-white transition-all duration-300 outline-none text-gray-900 text-sm font-medium shadow-sm`}
                  placeholder="name@pharmacy.com"
                  value={email}
                  onChange={(e) => {
                    setEmail(e.target.value);
                    if (errors.email) setErrors(prev => ({ ...prev, email: "" }));
                  }}
                />
              </div>
              {errors.email && <p className="mt-1 text-xs text-red-500 font-semibold ml-2">{errors.email}</p>}
            </div>

            <div className="space-y-1.5">
              <label className="block text-xs font-bold text-gray-700 ml-1 uppercase tracking-wider">Password</label>
              <div className="relative group">
                <div className="absolute inset-y-0 left-0 pl-3.5 flex items-center pointer-events-none">
                  <HiOutlineLockClosed className="h-5 w-5 text-gray-400 group-focus-within:text-teal-600 transition-colors" />
                </div>
                <input
                  type={showPassword ? "text" : "password"}
                  className={`block w-full pl-10 pr-11 py-3 border-2 ${errors.password ? 'border-red-500 bg-red-50/10' : 'border-gray-200 bg-gray-50/50 group-hover:border-gray-300'} rounded-xl focus:ring-4 focus:ring-teal-500/10 focus:border-teal-500 focus:bg-white transition-all duration-300 outline-none text-gray-900 text-sm font-medium shadow-sm`}
                  placeholder="••••••••"
                  value={password}
                  onChange={(e) => {
                    setPassword(e.target.value);
                    if (errors.password) setErrors(prev => ({ ...prev, password: "" }));
                  }}
                />
                <button
                  type="button"
                  className="absolute inset-y-0 right-0 pr-3.5 flex items-center text-gray-400 hover:text-teal-600 transition-colors"
                  onClick={() => setShowPassword(!showPassword)}
                >
                  {showPassword ? <HiOutlineEyeOff size={20} /> : <HiOutlineEye size={20} />}
                </button>
              </div>
              {errors.password && <p className="mt-1 text-xs text-red-500 font-semibold ml-2">{errors.password}</p>}
            </div>

            <div className="flex items-center justify-between py-1">
              <label className="flex items-center cursor-pointer select-none group">
                <div className="relative">
                  <input type="checkbox" className="sr-only peer" />
                  <div className="w-4 h-4 border-2 border-gray-300 rounded group-hover:border-teal-500 peer-checked:bg-teal-600 peer-checked:border-teal-600 transition-all duration-200 flex items-center justify-center">
                    <svg className="w-3 h-3 text-white hidden peer-checked:block" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M5 13l4 4L19 7" />
                    </svg>
                  </div>
                </div>
                <span className="ml-2.5 text-xs font-semibold text-gray-700 group-hover:text-gray-900 transition-colors">Remember me</span>
              </label>
              <Link
                to="/forgot"
                className="text-xs font-bold text-teal-600 hover:text-teal-700 transition-colors hover:underline underline-offset-4"
              >
                Forgot Password?
              </Link>
            </div>

            <div className="pt-1">
              <button
                type="submit"
                disabled={loading}
                className={`w-full py-3 px-6 rounded-xl text-white font-bold text-sm shadow-md shadow-teal-600/20 transform transition-all active:scale-[0.98] ${loading
                  ? 'bg-gray-400 cursor-not-allowed shadow-none'
                  : 'bg-teal-600 hover:bg-teal-700 hover:shadow-teal-600/40'
                  }`}
              >
                {loading ? (
                  <span className="flex items-center justify-center">
                    <svg className="animate-spin -ml-1 mr-2.5 h-5 w-5 text-white" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Signing in...
                  </span>
                ) : "Sign In"}
              </button>
            </div>
          </form>

          <div className="mt-8 pt-5 border-t border-gray-100">
            <p className="text-center text-gray-600 font-semibold text-xs">
              Don't have an account yet? <Link to="#" className="text-teal-600 font-bold hover:text-teal-700 transition-colors hover:underline">Contact Administration</Link>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Login;
